import { Calendar, IdCard, Pencil, Pin, Trash2, UserPlus } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router';
import { PageHeader } from '@components/page-header';
import { PageTabs } from '@components/page-tabs';
import { ReactColumn, SortingRule, TableComponent } from '@components/table';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { getCdnImage } from '@components/utils/cdn-image';
import { I18n } from '@core/i18n';
import { injectTSDI } from '@core/tsdi';
import { ChallengeSubscriptionType } from '@api/ChallengeClient';
import { AddParticipantDialog } from './add-participant-dialog';
import { EditDisplaySettingsDialog } from './edit-display-settings-dialog';
import { EditJoinDateDialog } from './edit-join-date-dialog';
import { EditResultDialog } from './edit-result-dialog';
import { EditTeamResultDialog } from './edit-team-result-dialog';
import { challengeRankingLabel, formatChallengeResult } from './raw-value';
import { RemoveEntryDialog } from './remove-entry-dialog';
import { resolveRoleLabel, toDataUri } from './role-display';
import { useStore } from './store';
import {
    ChallengeLeaderboardEntry,
    ChallengeTeamLeaderboardEntry
} from './types';

const tabs = [
    { label: 'Teams', id: 'teams' },
    { label: 'Members', id: 'members' }
] as const;

const individualTab = { label: 'Individual', id: 'individual' } as const;

const Avatar = ({ src }: { src?: string }) => (
    <div className="size-8 shrink-0 overflow-hidden rounded-full bg-muted">
        {src && <img src={src} className="size-full object-cover" alt="" />}
    </div>
);

const RowAction = ({
    title,
    icon,
    onClick
}: {
    title: string;
    icon: React.ReactNode;
    onClick(): void;
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                variant="ghost"
                size="icon-sm"
                aria-label={title}
                onClick={(event) => {
                    event.stopPropagation();
                    onClick();
                }}
            >
                {icon}
            </Button>
        </TooltipTrigger>
        <TooltipContent>{title}</TooltipContent>
    </Tooltip>
);

export const ChallengeLeaderboard = () => {
    const { challengeId } = useParams();
    const numericChallengeId = Number(challengeId);
    const {
        leaderboardEntries,
        teamLeaderboardEntries,
        individualLeaderboardEntries,
        challengeDetails,
        teamsEnabled,
        individualLeaderboardEnabled,
        dateTimeRange,
        resultFormat,
        updateJoinDate,
        updateResult,
        updateDisplaySettings,
        teamDailyResults,
        updateTeamDailyResults,
        teamMemberResults,
        updateTeamMemberResults,
        dailyAverageTeams,
        sumTeams,
        addEntry,
        removeEntry,
        removeTeamEntry,
        dispatch,
        refresh
    } = useStore(numericChallengeId);
    const { formatDateTime } = injectTSDI(I18n);

    const [tab, setTab] = React.useState<'members' | 'teams' | 'individual'>(
        'members'
    );
    const visibleTabs = individualLeaderboardEnabled
        ? [...tabs, individualTab]
        : [...tabs];
    const showsIndividualLeaderboard = tab === 'individual';
    const showsTeamMembers = teamsEnabled && !showsIndividualLeaderboard;
    const [joinDateEntry, setJoinDateEntry] =
        React.useState<ChallengeLeaderboardEntry | null>(null);
    const [resultEntry, setResultEntry] =
        React.useState<ChallengeLeaderboardEntry | null>(null);
    const [teamResultEntry, setTeamResultEntry] =
        React.useState<ChallengeTeamLeaderboardEntry | null>(null);
    const [displaySettingsEntry, setDisplaySettingsEntry] =
        React.useState<ChallengeLeaderboardEntry | null>(null);
    const [removalEntry, setRemovalEntry] =
        React.useState<ChallengeLeaderboardEntry | null>(null);
    const [teamRemovalEntry, setTeamRemovalEntry] =
        React.useState<ChallengeTeamLeaderboardEntry | null>(null);
    const [addOpen, setAddOpen] = React.useState(false);

    React.useEffect(() => {
        if (teamsEnabled) {
            setTab('teams');
        }
    }, [teamsEnabled]);

    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            dispatch({ type: 'sort', sort: sorting });
        }
    };

    const resultHeader = challengeRankingLabel(resultFormat)
        ? `Result (${challengeRankingLabel(resultFormat)})`
        : 'Result';

    const description = challengeDetails.$
        ? [challengeDetails.$.community?.name, challengeDetails.$.status]
              .filter(Boolean)
              .join(' · ')
        : undefined;

    return (
        <>
            <PageHeader
                title={
                    challengeDetails.$?.presentation.name.en ??
                    'Challenge Leaderboard'
                }
                description={description}
                actions={
                    <Button onClick={() => setAddOpen(true)}>
                        <UserPlus />
                        Add participant
                    </Button>
                }
            />

            {teamsEnabled && (
                <div className="mb-6">
                    <PageTabs
                        tabs={visibleTabs}
                        value={visibleTabs.findIndex(({ id }) => id === tab)}
                        onChange={(index) => setTab(visibleTabs[index].id)}
                    />
                </div>
            )}

            {teamsEnabled && tab === 'teams' ? (
                <TableComponent<ChallengeTeamLeaderboardEntry[]>
                    key="teams"
                    items={teamLeaderboardEntries}
                    tableLayout="fixed"
                    columns={[
                        {
                            Header: 'Position',
                            accessor: 'position',
                            style: { width: 90 },
                            disableSortBy: true
                        },
                        {
                            Header: 'Team',
                            id: 'team',
                            style: { width: 260 },
                            disableSortBy: true,
                            accessor: (entry) => (
                                <div className="flex items-center gap-3">
                                    <Avatar
                                        src={getCdnImage(
                                            entry.teamImage.bucketName,
                                            entry.teamImage.objectKey,
                                            {
                                                resize: {
                                                    width: 48,
                                                    height: 48
                                                }
                                            }
                                        )}
                                    />
                                    <span className="truncate">
                                        {entry.teamName}
                                    </span>
                                </div>
                            )
                        },
                        {
                            Header: resultHeader,
                            id: 'result',
                            style: { width: 140 },
                            disableSortBy: true,
                            accessor: (entry) =>
                                formatChallengeResult(
                                    entry.result,
                                    resultFormat
                                )
                        },
                        {
                            Header: 'Members',
                            accessor: 'memberCount',
                            style: { width: 100 },
                            disableSortBy: true
                        },
                        {
                            Header: 'Won Date',
                            id: 'wonDate',
                            style: { width: 170 },
                            disableSortBy: true,
                            accessor: (entry) =>
                                entry.wonDate
                                    ? formatDateTime(entry.wonDate)
                                    : ''
                        },
                        {
                            Header: '',
                            id: 'actions',
                            style: { width: 112 },
                            disableSortBy: true,
                            Cell: ({ row }) => (
                                <div className="flex justify-end gap-1">
                                    {(dailyAverageTeams || sumTeams) && (
                                        <RowAction
                                            title={
                                                dailyAverageTeams
                                                    ? 'Edit daily results'
                                                    : 'Edit member results'
                                            }
                                            icon={<Pencil />}
                                            onClick={() => {
                                                setTeamResultEntry(
                                                    row.original
                                                );
                                                const request =
                                                    dailyAverageTeams
                                                        ? teamDailyResults
                                                        : teamMemberResults;
                                                request.request([
                                                    row.original
                                                        .teamSubscriptionId
                                                ]);
                                            }}
                                        />
                                    )}
                                    <RowAction
                                        title="Remove team"
                                        icon={<Trash2 />}
                                        onClick={() =>
                                            setTeamRemovalEntry(row.original)
                                        }
                                    />
                                </div>
                            )
                        }
                    ]}
                    onChangePage={(page) => dispatch({ type: 'page', page })}
                    onPageSizeChange={(size) =>
                        dispatch({ type: 'size', size })
                    }
                    onSortingChange={onSortingChange}
                />
            ) : (
                <TableComponent<ChallengeLeaderboardEntry[]>
                    key={tab}
                    items={
                        showsIndividualLeaderboard
                            ? individualLeaderboardEntries
                            : leaderboardEntries
                    }
                    tableLayout="fixed"
                    columns={[
                        ...(showsTeamMembers
                            ? []
                            : [
                                  {
                                      Header: 'Position',
                                      accessor: 'position',
                                      style: { width: 90 },
                                      disableSortBy: true
                                  } as ReactColumn<ChallengeLeaderboardEntry>
                              ]),
                        {
                            Header: 'Member',
                            id: 'member',
                            style: { width: 320 },
                            disableSortBy: true,
                            accessor: (entry) => {
                                const roleLabel = resolveRoleLabel(
                                    entry.roleLabel
                                );

                                return (
                                    <div className="flex items-center gap-3">
                                        <div className="flex w-5 shrink-0">
                                            {entry.pinned && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Pin className="size-4 text-primary" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        Pinned to top of
                                                        leaderboard
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                        <Avatar
                                            src={getCdnImage(
                                                entry.userImage.bucketName,
                                                entry.userImage.objectKey,
                                                {
                                                    resize: {
                                                        width: 48,
                                                        height: 48
                                                    }
                                                }
                                            )}
                                        />
                                        <div className="min-w-0">
                                            <div className="flex min-w-0 items-center gap-1">
                                                <span className="truncate">
                                                    {entry.userName}
                                                </span>
                                                {entry.roleIconSvg && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <img
                                                                src={toDataUri(
                                                                    entry.roleIconSvg
                                                                )}
                                                                alt=""
                                                                className="size-5 shrink-0"
                                                            />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {roleLabel ||
                                                                'Role icon'}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            <div className="flex min-w-0 items-center gap-1.5">
                                                <span className="truncate text-sm text-muted-foreground">
                                                    {entry.userId}
                                                </span>
                                                {roleLabel && (
                                                    <Badge
                                                        variant="secondary"
                                                        className="px-1.5 text-[0.7rem]"
                                                    >
                                                        {roleLabel}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                        },
                        ...(showsTeamMembers
                            ? [
                                  {
                                      Header: 'Team',
                                      id: 'team',
                                      style: { width: 160 },
                                      disableSortBy: true,
                                      accessor: (
                                          entry: ChallengeLeaderboardEntry
                                      ) => entry.teamName ?? ''
                                  }
                              ]
                            : []),
                        {
                            Header: resultHeader,
                            accessor: (entry) =>
                                formatChallengeResult(
                                    entry.result,
                                    resultFormat
                                ),
                            id: 'result',
                            style: { width: 120 },
                            disableSortBy: true
                        },
                        {
                            Header: 'Join Timestamp',
                            accessor: (entry) =>
                                formatDateTime(entry.joinTimestamp),
                            id: 'joinTimestamp',
                            style: { width: 190 },
                            disableSortBy: true
                        },
                        {
                            Header: 'Won Date',
                            accessor: (entry) =>
                                entry.wonDate
                                    ? formatDateTime(entry.wonDate)
                                    : '',
                            id: 'wonDate',
                            style: { width: 170 },
                            disableSortBy: true
                        },
                        {
                            Header: '',
                            id: 'actions',
                            style: { width: 150 },
                            disableSortBy: true,
                            Cell: ({ row }) => (
                                <div className="flex justify-end gap-1">
                                    <RowAction
                                        title="Display settings"
                                        icon={<IdCard />}
                                        onClick={() =>
                                            setDisplaySettingsEntry(
                                                row.original
                                            )
                                        }
                                    />
                                    {!showsTeamMembers && (
                                        <RowAction
                                            title="Edit join timestamp"
                                            icon={<Calendar />}
                                            onClick={() =>
                                                setJoinDateEntry(row.original)
                                            }
                                        />
                                    )}
                                    <RowAction
                                        title="Edit result"
                                        icon={<Pencil />}
                                        onClick={() =>
                                            setResultEntry(row.original)
                                        }
                                    />
                                    <RowAction
                                        title="Remove from leaderboard"
                                        icon={<Trash2 />}
                                        onClick={() =>
                                            setRemovalEntry(row.original)
                                        }
                                    />
                                </div>
                            )
                        }
                    ]}
                    onChangePage={(page) => dispatch({ type: 'page', page })}
                    onPageSizeChange={(size) =>
                        dispatch({ type: 'size', size })
                    }
                    onSortingChange={onSortingChange}
                />
            )}

            <EditDisplaySettingsDialog
                entry={displaySettingsEntry}
                loading={updateDisplaySettings.loading}
                onClose={() => setDisplaySettingsEntry(null)}
                onSubmit={async (subscriptionId, settings) => {
                    await updateDisplaySettings.request([
                        subscriptionId,
                        settings
                    ]);
                    setDisplaySettingsEntry(null);
                    refresh();
                }}
            />
            <EditJoinDateDialog
                entry={joinDateEntry}
                loading={updateJoinDate.loading}
                onClose={() => setJoinDateEntry(null)}
                onSubmit={async (subscriptionId, createdDate) => {
                    await updateJoinDate.request([subscriptionId, createdDate]);
                    setJoinDateEntry(null);
                    refresh();
                }}
            />
            <EditResultDialog
                entry={resultEntry}
                resultFormat={resultFormat}
                requiresDay={dailyAverageTeams && !showsIndividualLeaderboard}
                dateTimeRange={dateTimeRange}
                loading={updateResult.loading}
                onClose={() => setResultEntry(null)}
                onSubmit={async (subscriptionId, value, day) => {
                    await updateResult.request([subscriptionId, value, day]);
                    setResultEntry(null);
                    refresh();
                }}
            />
            <EditTeamResultDialog
                entry={teamResultEntry}
                dailyResults={
                    dailyAverageTeams ? (teamDailyResults.$ ?? null) : null
                }
                memberResults={sumTeams ? (teamMemberResults.$ ?? null) : null}
                resultFormat={resultFormat}
                loading={teamDailyResults.loading || teamMemberResults.loading}
                saving={
                    updateTeamDailyResults.loading ||
                    updateTeamMemberResults.loading
                }
                onClose={() => setTeamResultEntry(null)}
                onSubmit={async (teamSubscriptionId, submission) => {
                    if (submission.days) {
                        await updateTeamDailyResults.request([
                            teamSubscriptionId,
                            submission.days
                        ]);
                    } else if (submission.members) {
                        await updateTeamMemberResults.request([
                            teamSubscriptionId,
                            submission.members
                        ]);
                    }
                    setTeamResultEntry(null);
                    refresh();
                }}
            />
            <AddParticipantDialog
                open={addOpen}
                communityId={challengeDetails.$?.community?.id}
                resultFormat={resultFormat}
                requiresDay={dailyAverageTeams && !showsIndividualLeaderboard}
                dateTimeRange={dateTimeRange}
                loading={addEntry.loading}
                onClose={() => setAddOpen(false)}
                onSubmit={async (userId, value, day) => {
                    await addEntry.request([
                        userId,
                        value,
                        day,
                        showsIndividualLeaderboard
                            ? ChallengeSubscriptionType.MEMBER
                            : undefined
                    ]);
                    setAddOpen(false);
                    refresh();
                }}
            />
            <RemoveEntryDialog
                name={removalEntry?.userName}
                loading={removeEntry.loading}
                onClose={() => setRemovalEntry(null)}
                onConfirm={async () => {
                    if (!removalEntry) {
                        return;
                    }
                    await removeEntry.request([removalEntry.subscriptionId]);
                    setRemovalEntry(null);
                    refresh();
                }}
            />
            <RemoveEntryDialog
                name={teamRemovalEntry?.teamName}
                loading={removeTeamEntry.loading}
                onClose={() => setTeamRemovalEntry(null)}
                onConfirm={async () => {
                    if (!teamRemovalEntry) {
                        return;
                    }
                    await removeTeamEntry.request([
                        teamRemovalEntry.teamSubscriptionId
                    ]);
                    setTeamRemovalEntry(null);
                    refresh();
                }}
            />
        </>
    );
};

export default ChallengeLeaderboard;
